import { SentryError } from '@ifixit/sentry';
import { IFixitAPIClient } from '@ifixit/ifixit-api-client';
import { useCallback, useEffect, useRef } from 'react';
import withShadowRoot from 'Shared/utils/shadow_dom';
import initializeComponent from 'Shared/react-initialize';
import { useAsync, IfPending, IfFulfilled, IfRejected } from 'react-async';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { useOnFirstView } from '@ifixit/analytics/hooks/use-track-first-view';
import { WPNewsletter } from 'Shared/newsletter';
import { getGrowthbookSync } from 'Shared/features';
import { getRecentlySeenMap, updateEntry, resetEntry } from './recently-seen-banners';

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const VIEW_DWELL_MS = 300;

const maxHeight = '500px';

const RenderBanner = ({ html, space }) => {
   const bannerRef = useRef(null);
   // useOnFirstView owns its own ref/observer for the view event. Bridge it to
   // bannerRef with a callback ref so the same node also drives the click,
   // newsletter, and recently-seen logic below.
   const viewTrackingRef = useOnFirstView(
      () => {
         const eventData = getBannerEventData(bannerRef, 'View');
         if (eventData) {
            trackAnalyticsCustomEvent(eventData);
         }
      },
      { dwellMs: VIEW_DWELL_MS }
   );
   const setBannerRef = useCallback(
      node => {
         bannerRef.current = node;
         viewTrackingRef.current = node;
      },
      [viewTrackingRef]
   );

   const intersectionCallback = entries => {
      const entry = entries[0];
      if (entry.isIntersecting) {
         updateRecentlySeenBanners(bannerRef);
      }
   };

   useEffect(() => {
      addGAtoLinks(bannerRef);
      labelBareLinks(bannerRef);
      setupAnyNewsletters(bannerRef);
      const observer = new IntersectionObserver(intersectionCallback, {
         threshold: 1,
      });
      if (bannerRef.current) {
         observer.observe(bannerRef.current);
      }
      return () => {
         removeGAfromLinks(bannerRef);
         observer.disconnect();
      };
      // eslint-disable-next-line react-hooks/refs
   }, [bannerRef.current]);

   return (
      <div
         ref={setBannerRef}
         style={{
            width: '100%',
            overflow: 'hidden',
            borderRadius: '16px',
            maxHeight: maxHeight,
            zIndex: 0,
            position: 'relative',
            marginTop: space,
            marginBottom: space,
         }}
         dangerouslySetInnerHTML={{ __html: html }}
      />
   );
};

const BannerWithShadowDom = withShadowRoot(RenderBanner);

const getHtmlFromApi = props => {
   const { type } = props;
   const currentUrl = new URL(window.location.href);
   const apiUrl = new URL('/campaigns', window.location.origin);

   // Add any existing query params
   for (let pair of currentUrl.searchParams.entries()) {
      apiUrl.searchParams.append(pair[0], pair[1]);
   }

   apiUrl.searchParams.append('currentUrl', currentUrl.toString());
   apiUrl.searchParams.append('type', type);

   const variant = getGrowthbookSync().getFeatureValue('campaign-banner-variant', '');
   if (variant) {
      apiUrl.searchParams.append('variant', variant);
   }

   const recentlySeenBanners = getRecentlySeenBanners();
   if (recentlySeenBanners.length > 0) {
      apiUrl.searchParams.append('excludePostids', JSON.stringify(recentlySeenBanners));
   }

   const apiClient = new IFixitAPIClient({
      origin: window.location.origin,
      headers: { 'Api-Client': 'iFixit-Web' },
   });
   const response = apiClient
      .get(`${apiUrl.pathname}${apiUrl.search}`, { statName: 'campaign-banner' })
      .then(response => {
         if (!response) {
            throw new SentryError('Failed to fetch HTML');
         }
         return response;
      });

   return response;
};

// Returns an array of postids seen [hideAfterViews] times or more and last seen within 30 days
const getRecentlySeenBanners = () => {
   const recentlySeenMap = getRecentlySeenMap();
   const recentlySeenBanners = [];

   for (const [postid, entry] of Object.entries(recentlySeenMap)) {
      const viewedMultipleTimes = entry.count >= 5;
      const seenRecently = entry.lastSeen > Date.now() - 7 * DAY_IN_MILLISECONDS;

      if (viewedMultipleTimes && seenRecently) {
         recentlySeenBanners.push(postid);
      } else if (!seenRecently) {
         resetEntry(postid);
      }
   }

   return recentlySeenBanners;
};

const updateRecentlySeenBanners = ref => {
   if (!ref.current) {
      return;
   }

   const postidNode = ref.current.querySelector('[data-postid]');
   const postid = postidNode?.dataset?.postid;

   if (!postid) {
      return;
   }

   // Only keep track of recently seen 'default' banners
   const isLargeBanner = postidNode.querySelector('.is-large');
   const isHomepageBanner = postidNode.querySelector('.is-homepage');
   const shouldUpdate = !isLargeBanner && !isHomepageBanner;

   if (shouldUpdate) {
      updateEntry(postid);
   }
};

const getBannerTitle = ref =>
   ref.current?.querySelector('[data-banner-title]')?.dataset?.bannerTitle;

const getBannerId = ref => ref.current?.querySelector('[data-postid]')?.dataset?.postid;

const setupAnyNewsletters = ref => {
   if (!ref.current) {
      return;
   }

   const title = getBannerTitle(ref) || 'Banner';

   ref.current.querySelectorAll('.newsletter-sub').forEach(newsLetterEl => {
      new WPNewsletter(newsLetterEl, title, 'Valkyrie Banner');
   });
};

// The WordPress banner HTML uses image/overlay links with no text content,
// which fails WCAG 4.1.2 (link-name). Name them after the banner title, which
// WordPress already localizes — there's no localized fallback to use when a
// banner ships without one, and an English name is worse than none.
const labelBareLinks = ref => {
   if (!ref.current) {
      return;
   }
   const title = getBannerTitle(ref);
   if (!title) {
      return;
   }
   ref.current.querySelectorAll('a').forEach(link => {
      const hasName =
         link.textContent.trim() ||
         link.getAttribute('aria-label') ||
         link.querySelector('img[alt]:not([alt=""])');
      if (!hasName) {
         link.setAttribute('aria-label', title);
      }
   });
};

const addGAtoLinks = ref => {
   const eventData = getBannerEventData(ref, 'Click');
   if (!eventData) {
      return;
   }

   const links = [...ref.current.querySelectorAll('a')];
   const handleClick = () => trackAnalyticsCustomEvent(eventData);

   links.forEach(link => {
      link.addEventListener('click', handleClick);
      link.eventHandler = handleClick;
   });
};

// One event name per action ('Campaign Banner - View' / '- Click') so GA4 mints
// two banner events total instead of one per banner. The banner is identified by
// the banner_id param (the same WordPress post id the recently-seen logic uses),
// with banner_title alongside for readability. Break the two events down by
// banner_id in reports / BigQuery for per-banner view and click counts, and by
// banner_system to tell these apart from the Strapi banners, which report the
// same two events with a banner_uid.
const getBannerEventData = (ref, action) => {
   const id = getBannerId(ref);
   if (!id) {
      return null;
   }

   return {
      eventCategory: 'Campaign Banner',
      eventAction: `Campaign Banner - ${action}`,
      banner_id: id,
      banner_title: getBannerTitle(ref),
      banner_system: 'wordpress',
   };
};

const removeGAfromLinks = ref => {
   if (!ref.current) {
      return;
   }
   const links = [...ref.current.querySelectorAll('a')];

   links.forEach(link => {
      if (link.eventHandler) {
         link.removeEventListener('click', link.eventHandler);
      }
   });
};

// Matches CampaignComponent.php's placeholder. An empty slot when Chrome restores
// a reload's scroll position makes the late banner push the reader down (#65721).
const Placeholder = ({ sizes, type }) => {
   const className = `wp-campaign-placeholder-${type}`;
   return (
      <>
         <style>{`
            .${className} { height: ${sizes.default}px; }
            @media screen and (min-width: ${sizes.small_width}px) {
               .${className} { height: ${sizes.small}px; }
            }
            @media screen and (min-width: ${sizes.intermediate_width}px) {
               .${className} { height: ${sizes.intermediate}px; }
            }
         `}</style>
         <div className={className} style={{ marginTop: sizes.space, marginBottom: sizes.space }} />
      </>
   );
};

export const WPCampaignBanner = props => {
   const { sizes, type } = props;
   const fallbackComponent = props.fallbackComponent || (() => null);
   // The homepage hero swaps in its own fallback with its own height.
   const reservesSpace = props.hasCampaigns && !props.fallbackComponent;
   const state = useAsync({ promiseFn: getHtmlFromApi, ...props });

   return (
      <>
         <IfPending state={state}>
            {() => (reservesSpace ? <Placeholder sizes={sizes} type={type} /> : null)}
         </IfPending>
         <IfRejected state={state}>{fallbackComponent}</IfRejected>
         <IfFulfilled state={state}>
            {html => <BannerWithShadowDom html={html} space={sizes.space} />}
         </IfFulfilled>
      </>
   );
};

initializeComponent('WPCampaignBanner', WPCampaignBanner);
